import { useEffect, useRef, useState } from "react";
import { Bell } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { formatDistanceToNow } from "date-fns";
import { getNotificationBadge } from "../../lib/notificationTypes";

export function ManagerNotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get("/manager/notifications", { params: { limit: 8 } });
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const markAllRead = async () => {
    await api.patch("/manager/notifications/read-all");
    fetchNotifications();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="manager-notification-bell-button"
        onClick={() => setOpen(!open)}
        title="Opens system notifications — new referrals, deal updates, and team activity."
        className="relative p-2 text-igc-cream hover:text-igc-goldText transition-colors"
      >
        <Bell size={22} weight={unreadCount > 0 ? "fill" : "duotone"} />
        {unreadCount > 0 && (
          <span data-testid="manager-notification-unread-count" className="absolute -top-1 -right-1 bg-igc-gold text-black text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-medium">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="manager-notification-dropdown" className="absolute right-0 mt-3 w-96 bg-igc-surface border border-igc-gold/20 shadow-2xl z-50 max-h-[28rem] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-igc-gold/10">
            <p className="font-serif text-igc-cream">Notifications</p>
            <button data-testid="manager-mark-all-read-button" onClick={markAllRead} title="Marks every notification here as read." className="text-xs text-igc-goldText hover:text-igc-goldHover">Mark all read</button>
          </div>
          {notifications.length === 0 && <p className="p-6 text-igc-muted text-sm text-center" data-testid="manager-notification-dropdown-empty">{loading ? "Loading..." : "No notifications yet."}</p>}
          {notifications.map((n) => (
            <div key={n.id} data-testid={`manager-notification-item-${n.id}`} className={`p-4 border-b border-igc-gold/5 border-l-2 ${n.read ? "border-l-transparent" : "border-l-igc-gold"}`}>
              <span className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 border mb-1.5 ${getNotificationBadge(n.type).cls}`}>
                {getNotificationBadge(n.type).label}
              </span>
              <p className="text-igc-cream text-sm">{n.message}</p>
              <p className="text-igc-muted text-xs mt-1">{formatDistanceToNow(new Date(n.timestamp), { addSuffix: true })}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
